// Growth › Leads: search, filters, sort and paging live in the URL
// (?q=&status=&bucket=&type=&state=&platform=&platform_id=&has_email=&activity=&verify=&signal=&sort=&page=),
// so a filtered view can be shared and survives Back from a lead. Selection is per
// page and drives the bulk status change, the bulk activity check and bulk Verify.
//
// One toolbar (owner, 2026-11-03: "too many quick filter buttons"): search, status,
// platform, verification (Needs verification is needs_review and unverified), a
// Filters button for the rest (type, state, email, bucket, activity, season), and the
// sort control. Above it, For review (LeadReview.tsx): the leads a check left for a
// person, to mark verified or failed one by one. The table keeps the columns that
// matter for working a lead, with a check by a verified lead's name; the rest is on
// the lead's page.

import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import { ACTIVITY_FILTERS, BUCKETS, LEAD_SIGNALS, LEAD_SORTS, LEAD_STATUSES, LEAD_TYPES, VERIFY_FILTERS, type ActivityFilter, type Bucket, type Lead, type LeadSignal, type LeadSort, type LeadStatus, type LeadType, type LeadWrite, type VerifyFilter } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  CheckIcon,
  ChevronRightIcon,
  ConfirmDialog,
  DataTable,
  EmailText,
  Dialog,
  EmptyState,
  ErrorState,
  FormField,
  InlineAlert,
  Pagination,
  PlusIcon,
  RelativeTime,
  SearchInput,
  SelectInput,
  SkeletonRows,
  SortControl,
  Textarea,
  TextInput,
  UploadIcon,
  useToast,
  type Column,
  type SortField,
} from '@/components/ui'
import { ACTIVITY_FILTER_LABELS, BUCKET_LABELS, KNOWN_PLATFORMS, LEAD_STATUS_LABELS, LEAD_TYPE_LABELS, SIGNAL_LABELS, VERIFY_FILTER_LABELS, VERIFY_RUN_WORDS } from '@/lib/growth'
import { useDocumentTitle, useIsDesktop } from '@/lib/hooks'
import { invalidateQueries, useQuery } from '@/lib/query'
import { useActivityRun } from '@/lib/useFinder'
import { ActivityRunBanner } from './activityShared'
import { LeadReview } from './LeadReview'
import { LeadStatusBadge } from './shared'
import { CurrentPlatform } from './verifyShared'
import { AddToCampaignDialog } from './AddToCampaign'

const PAGE_SIZE = 25

const TYPE_FILTERS = [{ value: '', label: 'Any type' }, ...LEAD_TYPES.map((t) => ({ value: t, label: LEAD_TYPE_LABELS[t] }))]
const BUCKET_FILTERS = [{ value: '', label: 'Any bucket' }, ...BUCKETS.map((b) => ({ value: b, label: BUCKET_LABELS[b] }))]

/** What the list sorts by, with the up/down arrow for the direction (SortControl). */
const SORT_FIELDS: SortField[] = [
  { value: 'priority', label: 'Priority', defaultDesc: true },
  { value: 'name', label: 'Name' },
  { value: 'created_at', label: 'Added', defaultDesc: true },
  { value: 'last_contacted_at', label: 'Last contacted', defaultDesc: true },
  { value: 'bucket', label: 'Bucket' },
]
const EMAIL_FILTERS = [
  { value: '', label: 'Email or not' },
  { value: 'true', label: 'Has email' },
  { value: 'false', label: 'No email' },
]
const ACTIVITY_OPTIONS = [{ value: '', label: 'Any activity' }, ...ACTIVITY_FILTERS.map((a) => ({ value: a, label: ACTIVITY_FILTER_LABELS[a] }))]
const SIGNAL_OPTIONS = [{ value: '', label: 'Any season signal' }, ...LEAD_SIGNALS.map((s) => ({ value: s, label: SIGNAL_LABELS[s] }))]

function readParams(p: URLSearchParams) {
  const status = p.get('status')
  const type = p.get('type')
  const sort = p.get('sort')
  const hasEmail = p.get('has_email')
  const activity = p.get('activity')
  const verify = p.get('verify')
  const signal = p.get('signal')
  const bucket = p.get('bucket')
  const platformId = p.get('platform_id')
  const page = Number(p.get('page'))
  return {
    q: p.get('q') ?? '',
    status: (LEAD_STATUSES as readonly string[]).includes(status ?? '') ? (status as LeadStatus) : undefined,
    bucket: (BUCKETS as readonly string[]).includes(bucket ?? '') ? (bucket as Bucket) : undefined,
    platform_id: platformId === 'none' ? ('none' as const) : Number(platformId) > 0 ? Number(platformId) : undefined,
    type: (LEAD_TYPES as readonly string[]).includes(type ?? '') ? (type as LeadType) : undefined,
    state: p.get('state') || undefined,
    platform: p.get('platform') || undefined,
    has_email: hasEmail === 'true' ? true : hasEmail === 'false' ? false : undefined,
    activity: (ACTIVITY_FILTERS as readonly string[]).includes(activity ?? '') ? (activity as ActivityFilter) : undefined,
    verify: (VERIFY_FILTERS as readonly string[]).includes(verify ?? '') ? (verify as VerifyFilter) : undefined,
    signal: (LEAD_SIGNALS as readonly string[]).includes(signal ?? '') ? (signal as LeadSignal) : undefined,
    sort: (LEAD_SORTS as readonly string[]).includes(sort ?? '') ? (sort as LeadSort) : ('-priority' as LeadSort),
    page: Number.isInteger(page) && page > 0 ? page : 1,
  }
}

function VerifiedMark({ byHand }: { byHand: boolean }) {
  const label = byHand ? 'Verified by hand' : 'Verified'
  return (
    <span title={label} className="ml-1 inline-flex align-[-1px] text-good">
      <CheckIcon size={13} role="img" aria-hidden={false} aria-label={label} />
    </span>
  )
}

function LeadName({ lead }: { lead: Lead }) {
  return (
    <span className="grid min-w-0 gap-0.5">
      <span className="break-words font-semibold">
        {lead.name}
        {/* A quiet mark, not a column: the select above filters on it. */}
        {lead.verify_status === 'verified' ? <VerifiedMark byHand={!!lead.verify_decision} /> : null}
      </span>
      <span className="truncate text-xs font-medium text-muted">{[lead.city, lead.state].filter(Boolean).join(', ') || lead.website?.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '') || 'No location'}</span>
    </span>
  )
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (e: ChangeEvent<HTMLInputElement>) => void }) {
  // A 44px tap target around a 16px box, so a thumb can't open the row by mistake.
  return (
    <label className="-m-3.5 inline-flex cursor-pointer items-center justify-center p-3.5">
      <input type="checkbox" aria-label={label} checked={checked} onChange={onChange} className="h-4 w-4 accent-accent-solid" />
    </label>
  )
}

export default function LeadsListPage() {
  useDocumentTitle('Leads')
  const isDesktop = useIsDesktop()
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = readParams(searchParams)
  const { q, status, bucket, type, state, platform, platform_id, has_email, activity, verify, signal, sort, page } = filters
  // The Filters panel: the less used filters. Open while any of them is on.
  const moreOn = [type, state, has_email, bucket, activity, signal].filter((v) => v !== undefined).length
  const [moreOpen, setMoreOpen] = useState(moreOn > 0)

  const update = (patch: Record<string, string | number | boolean | undefined>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === '' || (k === 'sort' && v === '-priority') || (k === 'page' && v === 1)) next.delete(k)
          else next.set(k, String(v))
        }
        if (!('page' in patch)) next.delete('page')
        return next
      },
      { replace: true },
    )

  const params = useMemo(
    () => ({ q, status, bucket, type, state, platform, platform_id, has_email, activity, verify, signal, sort, page, page_size: PAGE_SIZE }),
    [q, status, bucket, type, state, platform, platform_id, has_email, activity, verify, signal, sort, page],
  )
  const { data, error, status: loadStatus, isFetching, refetch } = useQuery(['growth', 'leads', params], (signal) => api.growth.leads.list(params, signal), { keepPrevious: true })
  const activityRun = useActivityRun()
  const verifyRun = useActivityRun('verify')

  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [adding, setAdding] = useState(false)
  const paramsKey = JSON.stringify(params)
  useEffect(() => setSelected(new Set()), [paramsKey])

  const rows = data?.leads ?? []
  const counts = data?.status_counts
  const bucketCounts = data?.bucket_counts
  const verifyCounts = data?.verify_counts
  const hasFilters = !!q || !!status || !!bucket || !!type || !!state || !!platform || !!platform_id || has_email !== undefined || !!activity || !!verify || !!signal

  // One select over two params: a linked platform by id, or text that links to nothing yet.
  const platformChoice = platform_id === 'none' ? 'none' : platform_id ? `id:${platform_id}` : platform ? `text:${platform}` : ''
  const linked = data?.facets.linked_platforms ?? []
  const chosen = platform_id && platform_id !== 'none' && !linked.some((p) => p.id === platform_id) ? [{ value: `id:${platform_id}`, label: 'This platform (0)' }] : []
  const looseText = (data?.facets.platforms ?? []).filter((t) => t !== 'unknown' && !linked.some((p) => p.name.toLowerCase() === t.toLowerCase()))
  const platformOptions = [
    { value: '', label: 'Any platform' },
    ...linked.map((p) => ({ value: `id:${p.id}`, label: `${p.name} (${p.count})` })),
    ...chosen,
    { value: 'none', label: `No platform (${data?.facets.no_platform ?? 0})` },
    ...looseText.map((t) => ({ value: `text:${t}`, label: `${t} (not linked)` })),
  ]
  const choosePlatform = (v: string) => {
    if (v.startsWith('id:')) update({ platform_id: v.slice(3), platform: undefined })
    else if (v.startsWith('text:')) update({ platform: v.slice(5), platform_id: undefined })
    else update({ platform_id: v || undefined, platform: undefined })
  }

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })
  const allSelected = rows.length > 0 && rows.every((l) => selected.has(l.id))

  const columns: Column<Lead>[] = [
    {
      id: 'select',
      header: <Check label="Select all on this page" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((l) => l.id)))} />,
      cell: (l) => <Check label={`Select ${l.name}`} checked={selected.has(l.id)} onChange={() => toggle(l.id)} />,
      className: 'w-10',
      hideOnMobile: true,
    },
    { id: 'name', header: 'Lead', primary: true, cell: (l) => <LeadName lead={l} />, className: 'min-w-[14rem] max-w-[22rem]' },
    { id: 'status', header: 'Status', cell: (l) => <LeadStatusBadge status={l.status} /> },
    { id: 'platform', header: 'Uses now', cell: (l) => <CurrentPlatform facts={l} linked={l.linked_platform} text={l.platform} /> },
    { id: 'email', header: 'Email', cell: (l) => (l.email ? <span className="font-mono text-xs [overflow-wrap:anywhere]"><EmailText email={l.email} /></span> : <span className="text-subtle">No email</span>), className: 'max-w-[16rem]' },
    { id: 'contacted', header: 'Last email', cell: (l) => <LastEmail lead={l} /> },
  ]

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        title="Leads"
        description={counts ? `${counts.all} ${counts.all === 1 ? 'organisation' : 'organisations'}${hasFilters ? ' match' : ' ScoreLM might sell to'}.` : 'Organisations ScoreLM might sell to.'}
        actions={
          <>
            <ButtonLink to="/growth/leads/import" icon={<UploadIcon size={16} />}>
              Import CSV
            </ButtonLink>
            <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => setAdding(true)}>
              Add lead
            </Button>
          </>
        }
      />
      <NewLeadDialog open={adding} onClose={() => setAdding(false)} />
      <LeadReview onShowAll={() => update({ verify: 'needs_review' })} onShowUnchecked={() => update({ verify: 'unverified' })} />

      <div className="grid gap-3">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <SearchInput label="Search leads" placeholder="Search name, email, contact, city or website" value={q} onChange={(v) => update({ q: v })} className="lg:max-w-sm lg:flex-1" />
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center lg:ml-auto">
            <SelectInput
              aria-label="Filter by status"
              value={status ?? ''}
              options={[
                { value: '', label: counts ? `Any status (${counts.all})` : 'Any status' },
                ...LEAD_STATUSES.map((st) => ({ value: st, label: counts ? `${LEAD_STATUS_LABELS[st]} (${counts[st]})` : LEAD_STATUS_LABELS[st] })),
              ]}
              onChange={(e) => update({ status: e.target.value })}
              className="h-8 sm:w-40"
            />
            <SelectInput aria-label="Filter by platform" value={platformChoice} options={platformOptions} onChange={(e) => choosePlatform(e.target.value)} className="h-8 sm:w-44" />
            <SelectInput
              aria-label="Filter by verification"
              value={verify ?? ''}
              options={[
                { value: '', label: verifyCounts ? `Any verification (${verifyCounts.all})` : 'Any verification' },
                ...VERIFY_FILTERS.map((v) => ({ value: v, label: verifyCounts ? `${VERIFY_FILTER_LABELS[v]} (${verifyCounts[v]})` : VERIFY_FILTER_LABELS[v] })),
              ]}
              onChange={(e) => update({ verify: e.target.value })}
              className="h-8 sm:w-48"
            />
            <Button size="sm" aria-expanded={moreOpen} aria-controls="lead-more-filters" onClick={() => setMoreOpen((o) => !o)} className="h-8">
              {moreOn ? `Filters · ${moreOn}` : 'Filters'}
            </Button>
            <SortControl label="Sort leads" fields={SORT_FIELDS} value={sort} onChange={(v) => update({ sort: v })} />
          </div>
        </div>
        {moreOpen ? (
          <div id="lead-more-filters" className="grid grid-cols-2 gap-2 rounded-lg border border-line bg-panel p-3 sm:grid-cols-3 lg:grid-cols-4">
            <SelectInput aria-label="Filter by type" value={type ?? ''} options={TYPE_FILTERS} onChange={(e) => update({ type: e.target.value })} className="h-8" />
            <SelectInput
              aria-label="Filter by state"
              value={state ?? ''}
              options={[{ value: '', label: 'Any state' }, ...(data?.facets.states ?? []).map((st) => ({ value: st, label: st }))]}
              onChange={(e) => update({ state: e.target.value })}
              className="h-8"
            />
            <SelectInput aria-label="Filter by email" value={has_email === undefined ? '' : String(has_email)} options={EMAIL_FILTERS} onChange={(e) => update({ has_email: e.target.value })} className="h-8" />
            <SelectInput
              aria-label="Filter by bucket"
              value={bucket ?? ''}
              options={BUCKET_FILTERS.map((o) => ({ ...o, label: o.value && bucketCounts ? `${o.label} (${bucketCounts[o.value as Bucket]})` : o.label }))}
              onChange={(e) => update({ bucket: e.target.value })}
              className="h-8"
            />
            <SelectInput aria-label="Filter by activity" value={activity ?? ''} options={ACTIVITY_OPTIONS} onChange={(e) => update({ activity: e.target.value })} className="h-8" />
            <SelectInput aria-label="Filter by season signal" value={signal ?? ''} options={SIGNAL_OPTIONS} onChange={(e) => update({ signal: e.target.value })} className="h-8" />
            {hasFilters ? (
              <Button size="sm" variant="ghost" className="h-8 justify-self-start" onClick={() => update({ q: undefined, status: undefined, bucket: undefined, type: undefined, state: undefined, platform: undefined, platform_id: undefined, has_email: undefined, activity: undefined, verify: undefined, signal: undefined })}>
                Clear all filters
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <ActivityRunBanner follow={activityRun} />
      <ActivityRunBanner follow={verifyRun} words={VERIFY_RUN_WORDS} />
      {selected.size > 0 ? <BulkBar ids={[...selected]} onDone={() => setSelected(new Set())} activityRun={activityRun} verifyRun={verifyRun} /> : null}

      <Card flush className="overflow-hidden">
        {loadStatus === 'loading' ? (
          <SkeletonRows rows={6} label="Loading leads" />
        ) : loadStatus === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load leads" onRetry={refetch} />
        ) : rows.length === 0 ? (
          data && data.total > 0 ? (
            <EmptyState
              title="No results on this page"
              description={`Page ${page} is past the end. ${data.total} ${data.total === 1 ? 'lead' : 'leads'}${hasFilters ? ' match' : ' in all'}.`}
              action={
                <Button size="sm" onClick={() => update({ page: 1 })}>
                  Go to page 1
                </Button>
              }
            />
          ) : hasFilters ? (
            <EmptyState
              title="No leads match"
              description="Try another search or filter."
              action={
                <Button size="sm" onClick={() => update({ q: undefined, status: undefined, bucket: undefined, type: undefined, state: undefined, platform: undefined, platform_id: undefined, has_email: undefined, activity: undefined, verify: undefined, signal: undefined })}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No leads yet"
              description="Import a list or add the first one by hand."
              action={
                <ButtonLink to="/growth/leads/import" variant="primary" size="sm" icon={<UploadIcon size={14} />}>
                  Import CSV
                </ButtonLink>
              }
            />
          )
        ) : (
          <>
            {loadStatus === 'error' ? (
              <div role="alert" className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-bad-soft/40 px-4 py-2 text-xs text-bad">
                Couldn't refresh. Showing the last results.
                <Button size="sm" variant="ghost" onClick={refetch}>
                  Retry
                </Button>
              </div>
            ) : null}
            <DataTable
              caption="Leads"
              rows={rows}
              columns={columns}
              rowKey={(l) => l.id}
              // On a phone the card holds a checkbox, so the whole card can't be one link.
              rowHref={isDesktop ? (l) => `/growth/leads/${l.id}` : undefined}
              renderCard={(l) => <LeadCard lead={l} checked={selected.has(l.id)} onToggle={() => toggle(l.id)} />}
              busy={isFetching && loadStatus === 'success'}
            />
            <div className="border-t border-line px-4 py-3">
              <Pagination page={data?.page ?? page} pageSize={data?.page_size ?? PAGE_SIZE} total={data?.total ?? 0} noun="leads" onPageChange={(p) => update({ page: p })} />
            </div>
          </>
        )}
      </Card>
    </div>
  )
}

function LeadCard({ lead, checked, onToggle }: { lead: Lead; checked: boolean; onToggle: () => void }) {
  return (
    <div className="flex items-start gap-3">
      <div className="pt-0.5">
        <Check label={`Select ${lead.name}`} checked={checked} onChange={onToggle} />
      </div>
      <Link to={`/growth/leads/${lead.id}`} className="flex min-w-0 flex-1 items-center gap-3">
        <div className="grid min-w-0 flex-1 gap-1.5">
          <LeadName lead={lead} />
          <div className="flex flex-wrap items-center gap-1.5">
            <LeadStatusBadge status={lead.status} />
            {lead.linked_platform ? <Badge tone="outline">{lead.linked_platform.name}</Badge> : lead.platform && lead.platform !== 'unknown' ? <Badge tone="outline">{lead.platform}</Badge> : null}
          </div>
          {lead.email ? <span className="truncate font-mono text-xs text-muted">{lead.email}</span> : null}
        </div>
        <ChevronRightIcon size={16} className="shrink-0 text-subtle" />
      </Link>
    </div>
  )
}

/** The newest email either way: "They replied" when it's theirs, "You wrote" when it's ours (waiting on them). */
function LastEmail({ lead }: { lead: Lead }) {
  const last = lead.last_email
  if (!last) return lead.last_contacted_at ? <RelativeTime value={lead.last_contacted_at} className="text-muted" /> : <span className="text-subtle">Never</span>
  return (
    <span className="grid gap-0.5 text-[13px]">
      <span className={last.direction === 'in' ? 'font-semibold text-good' : 'text-ink'}>{last.direction === 'in' ? 'They replied' : last.dry_run ? 'Dry run' : 'You wrote'}</span>
      <RelativeTime value={last.at} className="text-xs text-muted" />
    </span>
  )
}

function BulkBar({ ids, onDone, activityRun, verifyRun }: { ids: number[]; onDone: () => void; activityRun: ReturnType<typeof useActivityRun>; verifyRun: ReturnType<typeof useActivityRun> }) {
  const toast = useToast()
  const [status, setStatus] = useState<LeadStatus | ''>('')
  const [confirming, setConfirming] = useState(false)
  const [checking, setChecking] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [adding, setAdding] = useState(false)
  const noun = `${ids.length} ${ids.length === 1 ? 'lead' : 'leads'}`

  return (
    <div role="region" aria-label="Bulk actions" className="flex flex-wrap items-center gap-2 rounded-lg border border-line-strong bg-raised px-3 py-2">
      <span className="text-[13px] font-semibold text-ink">{ids.length} selected</span>
      <SelectInput
        aria-label="New status"
        value={status}
        options={[{ value: '', label: 'Change status to…' }, ...LEAD_STATUSES.filter((s) => s !== 'do_not_contact').map((s) => ({ value: s, label: LEAD_STATUS_LABELS[s] }))]}
        onChange={(e) => setStatus(e.target.value as LeadStatus | '')}
        className="h-8 min-w-[11rem] flex-1 sm:w-48 sm:flex-none"
      />
      <Button size="sm" disabled={!status} onClick={() => setConfirming(true)}>
        Apply
      </Button>
      <Button size="sm" disabled={activityRun.busy} onClick={() => setChecking(true)}>
        Check activity
      </Button>
      <Button size="sm" disabled={verifyRun.busy} onClick={() => setVerifying(true)}>
        Verify
      </Button>
      <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
        Add to campaign…
      </Button>
      <Button size="sm" variant="ghost" onClick={onDone}>
        Clear
      </Button>
      <AddToCampaignDialog open={adding} ids={ids} onClose={() => setAdding(false)} onDone={onDone} />
      <ConfirmDialog
        open={checking}
        onClose={() => setChecking(false)}
        title={`Check ${noun}?`}
        description="It reads each lead's public pages in the background, slowly, and uses today's page budget. If the budget runs out it stops, and the rest wait."
        confirmLabel={`Check ${noun}`}
        tone="primary"
        onConfirm={async () => {
          const run = await api.growth.leads.checkActivity(ids)
          activityRun.started(run.id)
          onDone()
        }}
      />
      <ConfirmDialog
        open={verifying}
        onClose={() => setVerifying(false)}
        title={`Verify ${noun}?`}
        description="It reads where each league lives today, slowly and in the background, and uses today's page budget. It sends nothing to anyone. If the budget runs out it stops, and the rest wait."
        confirmLabel={`Verify ${noun}`}
        tone="primary"
        onConfirm={async () => {
          const run = await api.growth.leads.verify(ids)
          verifyRun.started(run.id)
          onDone()
        }}
      />
      {status ? (
        <ConfirmDialog
          open={confirming}
          onClose={() => setConfirming(false)}
          title={`Move ${noun} to ${LEAD_STATUS_LABELS[status]}?`}
          description="Leads who unsubscribed stay as they are."
          confirmLabel={`Move ${noun}`}
          onConfirm={async () => {
            const r = await api.growth.leads.bulkStatus(ids, status)
            invalidateQueries('growth')
            const skipped = r.skipped.length
            toast.success(`${r.updated.length} moved to ${LEAD_STATUS_LABELS[status]}${skipped ? `. ${skipped} on Do not contact stayed there.` : '.'}`)
            onDone()
          }}
        />
      ) : null}
    </div>
  )
}

const BLANK = { name: '', type: 'other', sport: '', city: '', state: '', website: '', platform: 'unknown', contact_name: '', email: '', phone: '', priority: '0', notes: '' }

function NewLeadDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const [form, setForm] = useState(BLANK)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [problem, setProblem] = useState<{ text: string; leadId?: number } | null>(null)
  const [pending, setPending] = useState(false)

  const set = (key: keyof typeof BLANK) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [key]: e.target.value }))
  const close = () => {
    if (pending) return
    setForm(BLANK)
    setErrors({})
    setProblem(null)
    onClose()
  }

  const submit = async (e?: FormEvent) => {
    e?.preventDefault()
    if (!form.name.trim()) {
      setErrors({ name: 'Give the lead a name.' })
      return
    }
    setErrors({})
    setProblem(null)
    setPending(true)
    const text = (v: string) => v.trim() || undefined
    const body: LeadWrite = {
      name: form.name.trim(),
      type: form.type as LeadType,
      platform: text(form.platform),
      sport: text(form.sport),
      city: text(form.city),
      state: text(form.state),
      website: text(form.website),
      contact_name: text(form.contact_name),
      email: text(form.email),
      phone: text(form.phone),
      priority: Number(form.priority),
      notes: text(form.notes),
    }
    try {
      const lead = await api.growth.leads.create(body)
      invalidateQueries('growth')
      setPending(false)
      setForm(BLANK)
      onClose()
      navigate(`/growth/leads/${lead.id}`)
    } catch (err) {
      setPending(false)
      if (isApiError(err) && err.code === 'duplicate_lead') {
        const leadId = Number(err.details?.lead_id)
        setProblem({ text: err.message, leadId: Number.isInteger(leadId) && leadId > 0 ? leadId : undefined })
      } else if (isApiError(err) && err.field) setErrors({ [err.field]: err.message })
      else setProblem({ text: errorMessage(err) })
    }
  }

  const field = (key: keyof typeof BLANK, label: string, props: { type?: string; hint?: string; required?: boolean; inputMode?: 'numeric' } = {}) => (
    <FormField label={label} required={props.required} hint={props.hint} error={errors[key] ?? null}>
      {({ id, describedBy, invalid }) => (
        <TextInput id={id} type={props.type} inputMode={props.inputMode} aria-describedby={describedBy} invalid={invalid} value={form[key]} onChange={set(key)} disabled={pending} />
      )}
    </FormField>
  )

  return (
    <Dialog
      open={open}
      onClose={close}
      dismissible={!pending}
      size="lg"
      title="Add lead"
      description="Public organisation contacts only. Nothing about players or minors."
      footer={
        <>
          <Button onClick={close} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" loading={pending} onClick={() => void submit()}>
            Add lead
          </Button>
        </>
      }
    >
      <form noValidate onSubmit={(e) => void submit(e)} className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">{field('name', 'Name', { required: true })}</div>
        <FormField label="Type">
          {({ id }) => <SelectInput id={id} value={form.type} options={LEAD_TYPES.map((t) => ({ value: t, label: LEAD_TYPE_LABELS[t] }))} onChange={set('type')} disabled={pending} />}
        </FormField>
        <FormField label="Current platform">
          {({ id }) => <SelectInput id={id} value={form.platform} options={KNOWN_PLATFORMS.map((p) => ({ value: p, label: p === 'unknown' ? 'Unknown' : p }))} onChange={set('platform')} disabled={pending} />}
        </FormField>
        {field('sport', 'Sport')}
        {field('website', 'Website')}
        {field('city', 'City')}
        {field('state', 'State')}
        {field('contact_name', 'Public contact name')}
        {field('email', 'Public contact email', { type: 'email' })}
        {field('phone', 'Public contact phone')}
        {field('priority', 'Priority', { inputMode: 'numeric', hint: '0 to 100.' })}
        <div className="sm:col-span-2">
          <FormField label="Notes">
            {({ id }) => <Textarea id={id} value={form.notes} onChange={set('notes')} disabled={pending} />}
          </FormField>
        </div>
        {problem ? (
          <InlineAlert tone="bad" className="sm:col-span-2">
            {problem.text}
            {problem.leadId ? (
              <>
                {' '}
                <Link to={`/growth/leads/${problem.leadId}`} className="font-semibold underline" onClick={close}>
                  Open that lead
                </Link>
              </>
            ) : null}
          </InlineAlert>
        ) : null}
      </form>
    </Dialog>
  )
}
